import { useEffect, useId, useMemo, useState } from "react";
import {
  Check,
  Copy,
  Download,
  ImagePlus,
  Link2,
  Mail,
  MessageSquare,
  Palette,
  Phone,
  QrCode,
  RotateCcw,
  Trash2,
  TriangleAlert,
  Type,
  User,
  Wifi,
} from "lucide-react";
import { useLocalStorage } from "@suite/core";
import { AdBanner, AdConsentLink, SocialLinks, useToast } from "@suite/ui";
import { ECC_LEVELS, TooMuchData, encode, type Ecc } from "./qr/encode.ts";
import { renderSvg } from "./qr/render.ts";
import {
  DEFAULT_STYLE,
  EYE_STYLES,
  MODULE_STYLES,
  type EyeStyle,
  type LogoShape,
  type ModuleStyle,
  type QrStyle,
} from "./qr/style.ts";
import { scanWarnings } from "./qr/scan.ts";
import {
  EMPTY,
  KINDS,
  buildPayload,
  suggestFilename,
  type Fields,
  type Kind,
} from "./content.ts";
import {
  canCopyImage,
  copyPng,
  download,
  filenameFor,
  rasterise,
  readLogo,
  svgBlob,
  type RasterFormat,
} from "./export.ts";
import { Explainer } from "./Explainer.tsx";

export const STORAGE_KEY = "otterqr:v1";

const HOME_URL = "https://ottercraft.dev";

/** Shown in the preview before there is anything to encode. */
const PLACEHOLDER = "https://ottercraft.dev";

const EXPORT_SIZES = [256, 512, 1024, 2048];

const KIND_ICONS: Record<Kind, typeof Link2> = {
  link: Link2,
  text: Type,
  wifi: Wifi,
  email: Mail,
  sms: MessageSquare,
  phone: Phone,
  vcard: User,
};

interface Saved {
  kind: Kind;
  fields: Fields;
  style: QrStyle;
  ecc: Ecc;
  /** Pixel width of the raster downloads. */
  size: number;
}

const INITIAL: Saved = {
  kind: "link",
  fields: EMPTY,
  style: DEFAULT_STYLE,
  ecc: "M",
  size: 1024,
};

/**
 * Fills in anything a stored state is missing. The version in useLocalStorage
 * already throws away a state from an older shape, but a half-written one (a
 * tab killed mid-save, storage truncated at the quota) would otherwise reach
 * the renderer as `style.fill === undefined` and take the app down.
 */
function withDefaults(saved: Saved | null | undefined): Saved {
  if (!saved) return INITIAL;
  return {
    kind: saved.kind ?? INITIAL.kind,
    fields: { ...EMPTY, ...saved.fields },
    style: {
      ...DEFAULT_STYLE,
      ...saved.style,
      fill: { ...DEFAULT_STYLE.fill, ...saved.style?.fill },
    },
    ecc: saved.ecc ?? INITIAL.ecc,
    size: saved.size ?? INITIAL.size,
  };
}

/**
 * Whether this browser will actually keep what the app writes. Private windows
 * and "block site data" throw on the first write, and useLocalStorage swallows
 * that so the tool keeps working — but then the page must not promise the work
 * is safe. Probed once, on mount.
 */
function storageWorks(): boolean {
  try {
    window.localStorage.setItem("otterqr:probe", "1");
    window.localStorage.removeItem("otterqr:probe");
    return true;
  } catch {
    return false;
  }
}

export function App() {
  const { toast } = useToast();
  const [saves] = useState(storageWorks);
  const [stored, setStored, { clear }] = useLocalStorage<Saved>(STORAGE_KEY, INITIAL, {
    version: 1,
  });
  const state = withDefaults(stored);
  const { kind, fields, style, ecc, size } = state;
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // All three update through the functional form. Two style changes can land
  // in one render cycle (a preset that sets shape and colour together, a click
  // while a keystroke is still in flight), and the second must build on the
  // first rather than on the `style` this render closed over.
  const patch = (next: Partial<Saved>) => setStored((prev) => ({ ...withDefaults(prev), ...next }));
  const setStyle = (next: Partial<QrStyle>) =>
    setStored((prev) => {
      const base = withDefaults(prev);
      return { ...base, style: { ...base.style, ...next } };
    });
  const setField = <K extends Kind>(k: K, next: Partial<Fields[K]>) =>
    setStored((prev) => {
      const base = withDefaults(prev);
      return { ...base, fields: { ...base.fields, [k]: { ...base.fields[k], ...next } } };
    });

  const payload = buildPayload(kind, fields[kind]);
  const empty = payload.trim() === "";

  // Encoding a 177-module code is well under a millisecond, so the preview can
  // simply follow every keystroke — no debounce, no worker, no loading state.
  const encoded = useMemo(() => {
    try {
      return { matrix: encode(empty ? PLACEHOLDER : payload, ecc), error: null as string | null };
    } catch (error) {
      return {
        matrix: null,
        error:
          error instanceof TooMuchData
            ? error.message
            : "That could not be turned into a QR code.",
      };
    }
  }, [payload, ecc, empty]);

  // Two strings, not one shared one. Both previews are always in the document
  // (which of them shows is CSS), and an SVG's gradient and clip-path ids are
  // document-wide: with one string rendered twice, `url(#p-f)` in the visible
  // copy resolves to the identical id inside the display:none copy, which
  // Chrome has not built a paint server for — so a gradient code drew nothing.
  const previewSvg = useMemo(
    () => (encoded.matrix ? renderSvg(encoded.matrix, style, { pixels: 640, idPrefix: "d" }) : ""),
    [encoded.matrix, style],
  );
  const barSvg = useMemo(
    () => (encoded.matrix ? renderSvg(encoded.matrix, style, { pixels: 320, idPrefix: "m" }) : ""),
    [encoded.matrix, style],
  );

  const warnings = useMemo(() => {
    const found = scanWarnings(style, ecc);
    // Private windows and blocked site data throw on the first write, and
    // useLocalStorage swallows that so the tool keeps working. Say so once,
    // here, rather than let a refresh be the way they find out.
    return saves
      ? found
      : [
          ...found,
          {
            id: "storage",
            text: "This browser will not let the page keep your work, so a refresh starts over. Download the code before you leave.",
          },
        ];
  }, [style, ecc, saves]);
  const dirty = !empty || JSON.stringify(style) !== JSON.stringify(DEFAULT_STYLE);

  const save = async (format: RasterFormat | "svg") => {
    if (!encoded.matrix || empty) return;
    setBusy(format);
    try {
      const svg = renderSvg(encoded.matrix, style, { pixels: size, idPrefix: "q" });
      const name = filenameFor(suggestFilename(kind, fields[kind], payload), format);
      download(
        format === "svg" ? svgBlob(svg) : await rasterise(svg, format, size, style.background),
        name,
      );
      toast(`${name} saved`, "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "That download did not work.", "warning");
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    if (!encoded.matrix || empty) return;
    try {
      await copyPng(renderSvg(encoded.matrix, style, { pixels: size, idPrefix: "q" }), size);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast("This browser would not let us copy the image.", "warning");
    }
  };

  const reset = () => {
    clear();
    toast("Cleared — back to a plain black code", "info");
  };

  const pickLogo = async (file: File) => {
    try {
      const href = await readLogo(file);
      setStored((prev) => {
        const base = withDefaults(prev);
        return {
          ...base,
          // A logo hides part of the code, so the level that can rebuild the
          // most of it comes on with it. Still a plain select afterwards.
          ecc: "H",
          style: {
            ...base.style,
            logo: { href, scale: 0.2, padding: 0.6, shape: "square", plate: true },
          },
        };
      });
    } catch (error) {
      toast(error instanceof Error ? error.message : "That image could not be read.", "warning");
    }
  };

  // overflow-x-clip on the outer div, not overflow-hidden: `hidden` would make
  // it the scrolling ancestor of everything inside, and a sticky preview inside
  // a box that does not itself scroll never sticks. `clip` trims the mascot the
  // same way without creating a scroll container.
  return (
    <div className="grain relative min-h-dvh overflow-x-clip bg-cream font-sans text-ink">
      <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-[1080px] flex-col px-5 pb-8 pt-0 sm:px-8 lg:px-10">
        {/* Header */}
        <header className="flex items-stretch justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-baseline gap-3.5">
            <h1 className="flex shrink-0 items-center gap-2.5 pt-6 font-serif text-[28px] font-semibold leading-none text-pine sm:text-[34px]">
              <img src="/logo.png" alt="" width="34" height="34" className="size-[28px] sm:size-[34px]" />
              OtterQR
              <span className="sr-only">: make a QR code in your browser, with your own style and logo</span>
            </h1>
            <div className="flex flex-1 items-end self-stretch border-b border-rule pb-2.5">
              <p className="hidden text-[13px] text-muted sm:block">
                styles, colours and a logo — drawn on your device
              </p>
            </div>
          </div>
        </header>

        {/* Headline + mascot */}
        {/* pb-3 below lg: the pinned bar starts right after this section, and
            without it the bar's opaque edge clips the headline's descenders. */}
        <section className="relative mt-5 pb-3 lg:mt-6 lg:pb-0">
          <img
            src="/otter/otter-mascot.png"
            alt=""
            aria-hidden="true"
            className="bob pointer-events-none absolute z-0 right-2 top-0 hidden h-[128px] w-auto drop-shadow-[0_10px_12px_rgba(46,30,16,0.2)] sm:block lg:right-8 lg:-top-1 lg:h-[150px]"
          />
          <h2 className="relative z-10 max-w-[820px] font-serif text-[40px] font-semibold leading-[1.0] tracking-[-0.01em] text-pine sm:text-[56px] lg:text-[64px] lg:leading-[0.98]">
            <Headline empty={empty} kind={kind} />
          </h2>
        </section>

        {/* The tool. Its own wrapper so the pinned bar below unsticks exactly
            where the tool ends, rather than following the reader down the FAQ. */}
        <div>
          {/* Small screens: the code rides along at the top of the window while
              the form scrolls under it. It sits outside the grid on purpose —
              a sticky element only travels as far as its own parent's box, and
              inside the grid that box would be the height of the bar itself. */}
          <PreviewBar
            svg={barSvg}
            faded={empty}
            error={encoded.error}
            warnings={warnings}
            background={style.background}
            busy={busy}
            copied={copied}
            onSave={save}
            onCopy={copy}
            size={size}
            onSize={(next) => patch({ size: next })}
            dirty={dirty}
            onReset={reset}
          />

          {/* No items-start: the preview's column spans both rows, and it can only
              pin while its own grid item is as tall as the form beside it. */}
          <div className="mt-5 grid gap-6 lg:mt-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-8">
            <div className="order-1 flex flex-col gap-5 lg:col-start-1 lg:row-start-1">
              <ContentCard
                kind={kind}
                fields={fields}
                onKind={(next) => patch({ kind: next })}
                onField={setField}
              />
            </div>

            <div className="hidden lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:block">
              <div className="lg:sticky lg:top-4">
                <PreviewCard
                  svg={previewSvg}
                  faded={empty}
                  error={encoded.error}
                  warnings={warnings}
                  background={style.background}
                  busy={busy}
                  copied={copied}
                  onSave={save}
                  onCopy={copy}
                  size={size}
                  onSize={(next) => patch({ size: next })}
                  dirty={dirty}
                  onReset={reset}
                />
              </div>
            </div>

            <div className="order-3 flex flex-col gap-5 lg:col-start-1 lg:row-start-2">
              <ShapeCard
                style={style}
                onModules={(modules) => setStyle({ modules })}
                onEyes={(eyes) => setStyle({ eyes })}
              />
              <ColourCard style={style} onChange={setStyle} />
              <LogoCard style={style} onChange={setStyle} onPick={pickLogo} />
              <OutputCard
                style={style}
                ecc={ecc}
                onMargin={(margin) => setStyle({ margin })}
                onEcc={(next) => patch({ ecc: next })}
              />
            </div>
          </div>
        </div>

        {/* Ad — directly under the tool. Renders nothing until a client id, slot and consent all exist. */}
        <AdBanner
          format="responsive"
          slot={import.meta.env["VITE_ADSENSE_SLOT"] as string | undefined}
          className="mx-auto mt-12 max-w-[728px]"
        />

        {/* What this is, how it works, FAQ — static content, also prerendered. */}
        <Explainer />

        {/* Footer */}
        <footer className="mt-16 flex flex-col gap-6 border-t border-rule pt-6">
          <ul className="flex min-w-0 flex-wrap items-end gap-x-8 gap-y-4">
            <Fact n="01" text="Drawn in your browser, never uploaded" img="/otter/otter-lock.png" />
            <Fact n="02" text="No account, no watermark, no expiry" img="/otter/otter-stream.png" />
            <Fact n="03" text="PNG, JPG, WebP and real vector SVG" img="/otter/otter-p2p.png" />
          </ul>
          <nav aria-label="Site" className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 border-t border-rule/60 pt-4">
            <a
              href={HOME_URL}
              className="flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-rule bg-surface pl-2 pr-3 text-[13px] font-medium text-soft transition-colors hover:border-pine hover:text-pine"
            >
              <img src="/logo.png" alt="" width="18" height="18" className="size-[18px]" />
              More tools
            </a>
            <SocialLinks className="mr-auto" />
            <p className="flex gap-4 whitespace-nowrap text-[12px] text-muted">
              <a href={`${HOME_URL}/about`} className="link">About</a>
              <a href={`${HOME_URL}/contact`} className="link">Contact</a>
              <a href="/privacy" className="link">Privacy</a>
              <a href="/terms" className="link">Terms</a>
              <AdConsentLink />
            </p>
          </nav>
        </footer>
      </div>
    </div>
  );
}

// ---- header pieces --------------------------------------------------------

function Headline({ empty, kind }: { empty: boolean; kind: Kind }) {
  if (empty) {
    return (
      <>
        Type something in,
        <br />
        <em className="ripple">get a code out.</em>
      </>
    );
  }
  if (kind === "wifi") {
    return (
      <>
        Your Wi-Fi, <em className="ripple">joined by camera.</em>
      </>
    );
  }
  if (kind === "vcard") {
    return (
      <>
        You, <em className="ripple">saved in one scan.</em>
      </>
    );
  }
  return (
    <>
      There it is — <em className="ripple">make it yours.</em>
    </>
  );
}

// ---- content --------------------------------------------------------------

function ContentCard({
  kind,
  fields,
  onKind,
  onField,
}: {
  kind: Kind;
  fields: Fields;
  onKind: (kind: Kind) => void;
  onField: <K extends Kind>(kind: K, next: Partial<Fields[K]>) => void;
}) {
  return (
    <Card title="What goes in the code" icon={<QrCode className="size-3.5 text-muted" />}>
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map((option) => {
          const Icon = KIND_ICONS[option.value];
          const active = option.value === kind;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onKind(option.value)}
              aria-pressed={active}
              className={`flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition-colors ${
                active
                  ? "border-pine-deep bg-pine text-cream"
                  : "border-rule bg-surface text-soft hover:border-pine hover:text-pine"
              }`}
            >
              <Icon className="size-3.5" aria-hidden="true" />
              {option.label}
            </button>
          );
        })}
      </div>

      <div className="mt-1 flex flex-col gap-3">
        {kind === "link" ? (
          <TextField
            label="Address"
            value={fields.link.url}
            onChange={(url) => onField("link", { url })}
            placeholder="ottercraft.dev/qr"
            type="url"
            hint="Anything without https:// in front gets it added."
          />
        ) : null}

        {kind === "text" ? (
          <TextArea
            label="Text"
            value={fields.text.text}
            onChange={(text) => onField("text", { text })}
            placeholder="Anything at all — a note, a serial number, a poem."
            rows={4}
          />
        ) : null}

        {kind === "wifi" ? (
          <>
            <TextField
              label="Network name (SSID)"
              value={fields.wifi.ssid}
              onChange={(ssid) => onField("wifi", { ssid })}
              placeholder="Otter Lodge"
            />
            <TextField
              label="Password"
              value={fields.wifi.password}
              onChange={(password) => onField("wifi", { password })}
              placeholder="•••••••••"
              disabled={fields.wifi.security === "nopass"}
            />
            <div className="flex flex-wrap items-end gap-4">
              <Choice
                label="Security"
                value={fields.wifi.security}
                onChange={(security) => onField("wifi", { security: security as "WPA" | "WEP" | "nopass" })}
                options={[
                  { value: "WPA", label: "WPA / WPA2 / WPA3" },
                  { value: "WEP", label: "WEP" },
                  { value: "nopass", label: "Open (no password)" },
                ]}
              />
              <Switch
                label="Hidden network"
                checked={fields.wifi.hidden}
                onChange={(hidden) => onField("wifi", { hidden })}
              />
            </div>
            <p className="text-[12px] leading-[1.55] text-muted">
              Scanning this offers to join the network. iOS and Android both do it from the camera;
              the password is inside the code, so treat the picture like the password itself.
            </p>
          </>
        ) : null}

        {kind === "email" ? (
          <>
            <TextField
              label="To"
              value={fields.email.to}
              onChange={(to) => onField("email", { to })}
              placeholder="hello@example.com"
              type="email"
            />
            <TextField
              label="Subject"
              value={fields.email.subject}
              onChange={(subject) => onField("email", { subject })}
              placeholder="Optional"
            />
            <TextArea
              label="Message"
              value={fields.email.body}
              onChange={(body) => onField("email", { body })}
              placeholder="Optional"
              rows={3}
            />
          </>
        ) : null}

        {kind === "sms" ? (
          <>
            <TextField
              label="Number"
              value={fields.sms.number}
              onChange={(number) => onField("sms", { number })}
              placeholder="+39 333 000 0000"
              type="tel"
            />
            <TextArea
              label="Message"
              value={fields.sms.message}
              onChange={(message) => onField("sms", { message })}
              placeholder="Optional — the phone fills it in, the person presses send"
              rows={3}
            />
          </>
        ) : null}

        {kind === "phone" ? (
          <TextField
            label="Number"
            value={fields.phone.number}
            onChange={(number) => onField("phone", { number })}
            placeholder="+39 333 000 0000"
            type="tel"
            hint="Scanning offers to call. Include the country code if it may be scanned abroad."
          />
        ) : null}

        {kind === "vcard" ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="First name"
                value={fields.vcard.first}
                onChange={(first) => onField("vcard", { first })}
                placeholder="Ada"
              />
              <TextField
                label="Last name"
                value={fields.vcard.last}
                onChange={(last) => onField("vcard", { last })}
                placeholder="Lovelace"
              />
              <TextField
                label="Organisation"
                value={fields.vcard.org}
                onChange={(org) => onField("vcard", { org })}
                placeholder="Optional"
              />
              <TextField
                label="Job title"
                value={fields.vcard.title}
                onChange={(title) => onField("vcard", { title })}
                placeholder="Optional"
              />
              <TextField
                label="Phone"
                value={fields.vcard.phone}
                onChange={(phone) => onField("vcard", { phone })}
                placeholder="+39 333 000 0000"
                type="tel"
              />
              <TextField
                label="Email"
                value={fields.vcard.email}
                onChange={(email) => onField("vcard", { email })}
                placeholder="ada@example.com"
                type="email"
              />
            </div>
            <TextField
              label="Website"
              value={fields.vcard.url}
              onChange={(url) => onField("vcard", { url })}
              placeholder="example.com"
              hint="A contact card holds a lot of text, so these codes come out dense. Print them big."
            />
          </>
        ) : null}
      </div>
    </Card>
  );
}

// ---- style ----------------------------------------------------------------

function ShapeCard({
  style,
  onModules,
  onEyes,
}: {
  style: QrStyle;
  onModules: (value: ModuleStyle) => void;
  onEyes: (value: EyeStyle) => void;
}) {
  // One small real code, encoded once, drawn in every style on offer — a
  // swatch that lies about what it will look like is worse than no swatch.
  const sample = useMemo(() => encode("OTTERQR", "L"), []);
  const swatch = (over: Partial<QrStyle>) =>
    renderSvg(
      sample,
      {
        ...style,
        ...over,
        margin: 1,
        logo: null,
        background: null,
        eyeColor: null,
        fill: { ...style.fill, kind: "solid" },
      },
      // Distinct per row as well as per option: "square" is both a module
      // style and an eye style, and two swatches must never share an id.
      { pixels: 96, idPrefix: over.modules ? `sm-${over.modules}` : `se-${over.eyes}` },
    );

  return (
    <Card title="Shape" icon={<QrCode className="size-3.5 text-muted" />}>
      <Legend>Modules</Legend>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {MODULE_STYLES.map((option) => (
          <Swatch
            key={option.value}
            label={option.label}
            active={style.modules === option.value}
            svg={swatch({ modules: option.value })}
            onClick={() => onModules(option.value)}
          />
        ))}
      </div>

      <Legend className="mt-4">Corners</Legend>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {EYE_STYLES.map((option) => (
          <Swatch
            key={option.value}
            label={option.label}
            active={style.eyes === option.value}
            svg={swatch({ eyes: option.value })}
            onClick={() => onEyes(option.value)}
          />
        ))}
      </div>
    </Card>
  );
}

function Swatch({
  label,
  active,
  svg,
  onClick,
}: {
  label: string;
  active: boolean;
  svg: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={label}
      className={`flex flex-col items-center gap-1 rounded-[10px] border p-2 transition-colors ${
        active ? "border-pine bg-pine/8" : "border-rule bg-surface hover:border-pine/60"
      }`}
    >
      {/* Generated by renderSvg from a fixed sample; no user text reaches it. */}
      <span className="qr-swatch block w-full" dangerouslySetInnerHTML={{ __html: svg }} />
      <span className={`text-[11px] leading-none ${active ? "font-semibold text-pine" : "text-muted"}`}>
        {label}
      </span>
    </button>
  );
}

function ColourCard({ style, onChange }: { style: QrStyle; onChange: (next: Partial<QrStyle>) => void }) {
  const gradient = style.fill.kind !== "solid";
  return (
    <Card title="Colour" icon={<Palette className="size-3.5 text-muted" />}>
      <div className="flex flex-wrap items-end gap-4">
        <ColourField
          label={gradient ? "From" : "Code"}
          value={style.fill.color}
          onChange={(color) => onChange({ fill: { ...style.fill, color } })}
        />
        {gradient ? (
          <ColourField
            label="To"
            value={style.fill.color2}
            onChange={(color2) => onChange({ fill: { ...style.fill, color2 } })}
          />
        ) : null}
        <Choice
          label="Fill"
          value={style.fill.kind}
          onChange={(kind) => onChange({ fill: { ...style.fill, kind: kind as QrStyle["fill"]["kind"] } })}
          options={[
            { value: "solid", label: "Solid" },
            { value: "linear", label: "Gradient" },
            { value: "radial", label: "Gradient from centre" },
          ]}
        />
      </div>

      {style.fill.kind === "linear" ? (
        <Range
          label="Gradient angle"
          value={style.fill.angle}
          min={0}
          max={350}
          step={10}
          format={(v) => `${v}°`}
          onChange={(angle) => onChange({ fill: { ...style.fill, angle } })}
        />
      ) : null}

      <div className="flex flex-wrap items-end gap-4">
        <ColourField
          label="Background"
          value={style.background ?? "#ffffff"}
          disabled={style.background === null}
          onChange={(background) => onChange({ background })}
        />
        <Switch
          label="Transparent"
          checked={style.background === null}
          onChange={(on) => onChange({ background: on ? null : "#ffffff" })}
        />
        <ColourField
          label="Corners"
          value={style.eyeColor ?? style.fill.color}
          disabled={style.eyeColor === null}
          onChange={(eyeColor) => onChange({ eyeColor })}
        />
        <Switch
          label="Match the code"
          checked={style.eyeColor === null}
          onChange={(on) => onChange({ eyeColor: on ? null : style.fill.color })}
        />
      </div>
      <p className="text-[12px] leading-[1.55] text-muted">
        A camera needs the code darker than what is behind it. Transparent PNGs keep whatever they
        are placed on, so check the contrast there too.
      </p>
    </Card>
  );
}

// ---- logo -----------------------------------------------------------------

function LogoCard({
  style,
  onChange,
  onPick,
}: {
  style: QrStyle;
  onChange: (next: Partial<QrStyle>) => void;
  onPick: (file: File) => void;
}) {
  const inputId = useId();
  const [dragging, setDragging] = useState(false);
  const logo = style.logo;

  return (
    <Card title="Logo" icon={<ImagePlus className="size-3.5 text-muted" />}>
      {logo ? (
        <>
          <div className="flex items-center gap-3">
            <span className="flex size-14 shrink-0 items-center justify-center rounded-[10px] border border-rule bg-[#fbfaf5] p-1.5">
              <img src={logo.href} alt="Your logo" className="max-h-full max-w-full object-contain" />
            </span>
            <div className="flex flex-wrap gap-2">
              <label
                htmlFor={inputId}
                className="flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-rule bg-surface px-3 text-[12.5px] font-medium text-soft transition-colors hover:border-pine hover:text-pine"
              >
                <ImagePlus className="size-3.5" aria-hidden="true" />
                Replace
              </label>
              <button
                type="button"
                onClick={() => onChange({ logo: null })}
                className="flex h-8 items-center gap-1.5 rounded-full border border-rule bg-surface px-3 text-[12.5px] font-medium text-soft transition-colors hover:border-danger hover:text-danger"
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
                Remove
              </button>
            </div>
          </div>

          <Range
            label="Size"
            value={Math.round(logo.scale * 100)}
            min={8}
            max={30}
            step={1}
            format={(v) => `${v}% of the code`}
            onChange={(v) => onChange({ logo: { ...logo, scale: v / 100 } })}
          />
          <Range
            label="Clear space"
            value={logo.padding}
            min={0}
            max={2}
            step={0.2}
            format={(v) => (v === 0 ? "none" : `${v.toFixed(1)} modules`)}
            onChange={(padding) => onChange({ logo: { ...logo, padding } })}
          />
          <div className="flex flex-wrap items-end gap-4">
            <Choice
              label="Shape"
              value={logo.shape}
              onChange={(shape) => onChange({ logo: { ...logo, shape: shape as LogoShape } })}
              options={[
                { value: "square", label: "Square" },
                { value: "rounded", label: "Rounded" },
                { value: "circle", label: "Circle" },
              ]}
            />
            <Switch
              label="Plate behind it"
              checked={logo.plate}
              onChange={(plate) => onChange({ logo: { ...logo, plate } })}
            />
          </div>
          <p className="text-[12px] leading-[1.55] text-muted">
            The modules under the logo are left undrawn rather than covered, so the code reads the
            same with or without the plate. Scan it before you print it.
          </p>
        </>
      ) : (
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files[0];
            if (file) onPick(file);
          }}
          className={`rounded-[14px] border-2 border-dashed transition-colors ${
            dragging ? "border-teal bg-teal/12" : "border-teal/45 bg-teal/[0.06]"
          }`}
        >
          <label
            htmlFor={inputId}
            className="flex cursor-pointer flex-col items-center justify-center gap-1 px-6 py-7 text-center"
          >
            <span className="font-serif text-[19px] font-semibold leading-none text-pine">
              Drop a logo in the middle
            </span>
            <span className="text-[13px] text-soft">
              or <span className="link font-medium text-teal-deep">choose a picture</span> — PNG, JPG or SVG
            </span>
            <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              Optional · stays on this device
            </span>
          </label>
        </div>
      )}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(file);
          e.target.value = "";
        }}
      />
    </Card>
  );
}

// ---- output ---------------------------------------------------------------

function OutputCard({
  style,
  ecc,
  onMargin,
  onEcc,
}: {
  style: QrStyle;
  ecc: Ecc;
  onMargin: (value: number) => void;
  onEcc: (value: Ecc) => void;
}) {
  return (
    <Card title="Fine print" icon={<RotateCcw className="size-3.5 text-muted" />}>
      <Range
        label="Quiet zone"
        value={style.margin}
        min={0}
        max={8}
        step={1}
        format={(v) => (v === 1 ? "1 module" : `${v} modules`)}
        onChange={onMargin}
      />
      <Choice
        label="Error correction"
        value={ecc}
        onChange={(value) => onEcc(value as Ecc)}
        options={ECC_LEVELS.map((level) => ({
          value: level.value,
          label: `${level.label} — survives ${level.recovers} damage`,
        }))}
      />
      <p className="text-[12px] leading-[1.55] text-muted">
        Higher correction means a denser code that still scans when part of it is covered, scuffed
        or replaced by a logo. Medium is the usual choice; High is for anything printed or
        stickered.
      </p>
    </Card>
  );
}

// ---- preview --------------------------------------------------------------

interface PreviewProps {
  svg: string;
  faded: boolean;
  error: string | null;
  warnings: { id: string; text: string }[];
  background: string | null;
  busy: string | null;
  copied: boolean;
  onSave: (format: RasterFormat | "svg") => void;
  onCopy: () => void;
  size: number;
  onSize: (value: number) => void;
  /** True when there is work a reset would actually throw away. */
  dirty: boolean;
  onReset: () => void;
}

/**
 * The code itself, at whatever size the box it is given is.
 *
 * The markup comes from renderSvg, which escapes every attribute it writes and
 * never embeds the payload text.
 */
function CodeBox({
  svg,
  faded,
  error,
  background,
  hint,
}: Pick<PreviewProps, "svg" | "faded" | "error" | "background"> & { hint: boolean }) {
  if (error) {
    return (
      <p className="flex h-full min-h-[120px] items-center justify-center px-4 text-center text-[13px] leading-[1.5] text-danger">
        {error}
      </p>
    );
  }
  return (
    <div className="relative">
      <div
        className={`qr-preview transition-opacity ${faded ? "opacity-25" : ""}`}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {faded && hint ? (
        <p className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center font-serif text-[17px] leading-tight text-pine">
          Your code appears here
          <br />
          as you type
        </p>
      ) : null}
    </div>
  );
}

function Warning({ text, className = "" }: { text: string; className?: string }) {
  return (
    <p
      className={`flex gap-2 rounded-[8px] border border-warn/40 bg-warn/8 px-3 py-2 text-[12px] leading-[1.5] text-soft ${className}`}
    >
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-warn" aria-hidden="true" />
      <span>{text}</span>
    </p>
  );
}

function SizeSelect({
  size,
  onSize,
  compact = false,
}: Pick<PreviewProps, "size" | "onSize"> & { compact?: boolean }) {
  const id = useId();
  return (
    <>
      <label htmlFor={id} className="sr-only">
        Download size
      </label>
      <select
        id={id}
        value={size}
        onChange={(e) => onSize(Number(e.target.value))}
        className="h-7 rounded-[8px] border border-rule bg-[#fbfaf5] px-1.5 font-mono text-[12px] text-ink focus:border-teal-deep focus:outline-none focus:ring-2 focus:ring-teal/30"
      >
        {EXPORT_SIZES.map((option) => (
          <option key={option} value={option}>
            {compact ? `${option} px` : `${option} × ${option}`}
          </option>
        ))}
      </select>
    </>
  );
}

/** SVG / JPG / WebP, plus Copy — the same row in both layouts. */
function OtherFormats({
  busy,
  copied,
  disabled,
  onSave,
  onCopy,
}: Pick<PreviewProps, "busy" | "copied" | "onSave" | "onCopy"> & { disabled: boolean }) {
  const copyable = useMemo(() => canCopyImage(), []);
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {(["svg", "jpeg", "webp"] as const).map((format) => (
        <button
          key={format}
          type="button"
          disabled={disabled || busy !== null}
          onClick={() => onSave(format)}
          className="link text-[12px] font-semibold text-pine disabled:opacity-40"
        >
          {busy === format ? "…" : `${format === "jpeg" ? "JPG" : format.toUpperCase()} ↓`}
        </button>
      ))}
      {copyable ? (
        <button
          type="button"
          disabled={disabled}
          onClick={onCopy}
          className="ml-auto flex items-center gap-1.5 text-[12px] font-semibold text-pine disabled:opacity-40"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      ) : null}
    </div>
  );
}

/** From lg up: the tall card in the right-hand column, pinned as the form scrolls. */
function PreviewCard(props: PreviewProps) {
  const { svg, faded, error, warnings, background, busy, size, onSave, onSize, dirty, onReset } = props;
  const disabled = faded || Boolean(error);
  return (
    <div className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between gap-3 border-b border-rule px-4 py-2">
        <span className="eyebrow">Your code</span>
        <StartOver dirty={dirty} onReset={onReset} />
      </div>

      <div className="flex flex-col gap-4 p-4">
        <div
          className={`overflow-hidden rounded-[10px] border border-rule ${
            background === null ? "checker" : ""
          }`}
        >
          <CodeBox svg={svg} faded={faded} error={error} background={background} hint />
        </div>

        {warnings.length > 0 && !disabled ? (
          <div className="flex flex-col gap-2">
            {warnings.map((warning) => (
              <Warning key={warning.id} text={warning.text} />
            ))}
          </div>
        ) : null}

        <TealPill onClick={() => onSave("png")} disabled={disabled || busy !== null} className="w-full">
          <Download className="size-3.5" />
          {busy === "png" ? "Drawing…" : "Download PNG"}
        </TealPill>

        <OtherFormats {...props} disabled={disabled} />

        <div className="flex items-center justify-between gap-3 border-t border-rule pt-3 text-[12px] font-medium text-soft">
          Download size
          <SizeSelect size={size} onSize={onSize} />
        </div>
        <p className="-mt-2 text-[11px] leading-[1.5] text-muted">
          SVG ignores this — it is vector, and prints sharp at any size.
        </p>
      </div>
    </div>
  );
}

/**
 * Below lg: a bar pinned to the top of the window, so the code stays in sight
 * however far down the form the reader has scrolled. The code is a button —
 * tapping it swaps the bar to a tall layout with a big code, for judging a
 * style or a logo, and tapping again puts it back.
 */
function PreviewBar(props: PreviewProps) {
  const { svg, faded, error, warnings, background, busy, size, onSave, onSize, dirty, onReset } = props;
  const [big, setBig] = useState(false);
  const disabled = faded || Boolean(error);
  const warning = warnings[0];

  return (
    <div className="sticky top-0 z-20 -mx-5 border-b border-rule bg-surface px-5 py-3 shadow-[0_8px_14px_-12px_rgba(46,30,16,0.9)] sm:-mx-8 sm:px-8 lg:hidden">
      <div className={big ? "flex flex-col items-center gap-3" : "flex items-start gap-3.5"}>
        <button
          type="button"
          onClick={() => setBig((v) => !v)}
          aria-expanded={big}
          aria-label={big ? "Show the code smaller" : "Show the code bigger"}
          className={`shrink-0 overflow-hidden rounded-[8px] border border-rule ${
            background === null ? "checker" : ""
          } ${big ? "w-[230px] max-w-full" : "w-[104px]"}`}
        >
          <CodeBox svg={svg} faded={faded} error={error} background={background} hint={false} />
        </button>

        <div className="flex w-full min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <SizeSelect size={size} onSize={onSize} compact />
            <StartOver dirty={dirty} onReset={onReset} compact />
          </div>
          <TealPill onClick={() => onSave("png")} disabled={disabled || busy !== null} className="w-full">
            <Download className="size-3.5" />
            {busy === "png" ? "Drawing…" : "Download PNG"}
          </TealPill>
          <OtherFormats {...props} disabled={disabled} />
        </div>
      </div>

      {faded && !error ? (
        <p className="mt-2 text-[12px] leading-[1.5] text-muted">
          Fill in the form below and the code draws itself up here.
        </p>
      ) : null}

      {/* Only the first caution: the bar has to stay a bar. The rest are one
          scroll away in the same place they are on a wide screen. */}
      {warning && !disabled ? <Warning text={warning.text} className="mt-2" /> : null}
    </div>
  );
}

// ---- small shared pieces --------------------------------------------------

function Card({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
        <span className="eyebrow">{title}</span>
        {icon}
      </div>
      <div className="flex flex-col gap-3.5 p-4">{children}</div>
    </section>
  );
}

function Legend({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <p className={`text-[12px] font-medium text-soft ${className}`}>{children}</p>;
}

const inputClass =
  "h-9 w-full rounded-[8px] border border-rule bg-[#fbfaf5] px-3 text-[13px] text-ink placeholder:text-muted/70 focus:border-teal-deep focus:outline-none focus:ring-2 focus:ring-teal/30 disabled:opacity-50";

function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  type = "text",
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  type?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={id} className="text-[12px] font-medium text-soft">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
      {hint ? <p className="text-[11px] leading-[1.5] text-muted">{hint}</p> : null}
    </div>
  );
}

function TextArea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[12px] font-medium text-soft">
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full resize-y rounded-[8px] border border-rule bg-[#fbfaf5] px-3 py-2 text-[13px] leading-[1.5] text-ink placeholder:text-muted/70 focus:border-teal-deep focus:outline-none focus:ring-2 focus:ring-teal/30"
      />
    </div>
  );
}

function Choice({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  const id = useId();
  return (
    <div className="flex min-w-[8rem] flex-1 flex-col gap-1.5">
      <label htmlFor={id} className="text-[12px] font-medium text-soft">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ColourField({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className={`flex flex-col gap-1.5 ${disabled ? "opacity-50" : ""}`}>
      <label htmlFor={id} className="text-[12px] font-medium text-soft">
        {label}
      </label>
      <div className="flex h-9 items-center gap-2 rounded-[8px] border border-rule bg-[#fbfaf5] pl-1.5 pr-2.5">
        <input
          id={id}
          type="color"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="size-6 cursor-pointer rounded border-0 bg-transparent p-0 disabled:cursor-default"
        />
        <span className="font-mono text-[12px] uppercase text-soft">{value}</span>
      </div>
    </div>
  );
}

function Switch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex h-9 items-center gap-2 text-[12px] text-soft">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-3.5 accent-pine"
      />
      {label}
    </label>
  );
}

function Range({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="flex items-baseline justify-between text-[12px] font-medium text-soft">
        {label}
        <span className="font-mono text-[11px] text-muted">{format(value)}</span>
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="fader w-full"
      />
    </div>
  );
}

/**
 * The one obvious way to throw the work away. It asks first — one click clears
 * the content *and* the design, and there is no undo — unless there is nothing
 * to lose, in which case asking is theatre.
 */
function StartOver({
  dirty,
  onReset,
  compact = false,
}: {
  dirty: boolean;
  onReset: () => void;
  /** Icon only, for the pinned bar where a whole word does not fit. */
  compact?: boolean;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      onClick={() => {
        if (armed || !dirty) {
          setArmed(false);
          onReset();
        } else {
          setArmed(true);
        }
      }}
      aria-label={armed ? "Clear everything and start over" : "Start over"}
      title={armed ? "Clear everything" : "Start over"}
      className={`flex h-7 shrink-0 items-center gap-1.5 rounded-full border text-[12px] font-medium transition-colors ${
        compact && !armed ? "w-7 justify-center" : "px-2.5"
      } ${
        armed
          ? "border-danger bg-danger/10 text-danger"
          : "border-rule bg-surface text-soft hover:border-pine hover:text-pine"
      }`}
    >
      <RotateCcw className="size-3.5" aria-hidden="true" />
      {compact ? (armed ? "Clear?" : null) : armed ? "Clear everything?" : "Start over"}
    </button>
  );
}

function TealPill({
  className = "",
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`flex h-9 items-center justify-center gap-2 rounded-full border-2 border-teal-deep bg-teal px-3.5 text-[12px] font-semibold text-cream shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_5px_-2px_rgba(31,63,57,0.6)] transition-opacity hover:bg-teal-deep disabled:opacity-40 disabled:hover:bg-teal ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

function Fact({ n, text, img }: { n: string; text: string; img: string }) {
  return (
    <li className="flex flex-col items-start gap-1.5">
      <img src={img} alt="" aria-hidden="true" className="h-9 w-auto" />
      <span className="flex items-baseline gap-2">
        <span className="font-mono text-[11px] text-pine">{n}</span>
        <span className="text-[13px] text-ink">{text}</span>
      </span>
    </li>
  );
}
